// Region-view (surface / subsurface) visuals + view-transition
// cinematography, extracted from GameBoard.jsx. Owns the per-frame
// visibility pass that hides surface cards in subsurface view, the
// launch/land card motion + shared opacity curve during a view
// transition, the water-body tint meshes, and the subsurface scene /
// ambience state sync. See the comment on the VIEW_TRANSITION consts
// for the cinematography rationale.

import { setSubsurfaceMix } from '../../../utils/arena/subsurfaceAmbience';
import { connectedSubsurfaceComponents, sampleCellGeometry } from '../../../utils/game/engine/core';
import { computeGridPositions } from '../../../utils/game/grid';
import { syncMeshFromBody } from '../../../utils/game/physicsWorld';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { createRegionTintMesh, disposeRegionTintMesh, updateRegionTintEdges, updateRegionTintGeometry } from '../../../utils/game/visualEffects/regionViewTint';

// v1.73. Region-view transition cinematography. The cards launch
// upward in world Y by a large fraction of the camera-to-table
// distance: the orbit camera lives ~160 world units above the table,
// so a card-scale lift (1–2 units) is invisible against that depth.
// The Y axis is approximately aligned with the camera's view ray
// (near-top-down orbit), so the lift reads on screen as motion
// *toward* the viewer. Perspective projection makes the card grow
// from ~1× to ~1.8× as its distance to the camera halves. Returning
// to surface mirrors this: cards spawn high (large + transparent),
// swoop down fast, then decelerate gently into their original rest
// position.
//
// Per-card random delay staggers takeoff so the board reads as a
// flock launching in rolling waves rather than a single rigid block.
// Cards fly straight up along the same trajectory; only the start
// time differs.
//
// All visuals tied to one card (face, HUD pills, status badges,
// ownership outline, hover stroke, particle effects) share one
// opacity curve so the card reads as a single moving object, not
// stacked layers peeling apart. The card materials' `alphaTest: 0.5`
// is temporarily cleared during the transition. Otherwise the
// fragment-shader discard kicks in around opacity 0.5 and the card
// pops to invisible while its HUDs and effects continue fading.
const VIEW_TRANSITION_DURATION_MS = 850;
const VIEW_TRANSITION_LIFT = 70;
const VIEW_TRANSITION_STAGGER_MS = 320;

// v1.72. Per-frame visibility flip for the region-view toggle.
// Surface mode: every mesh visible (default). Subsurface mode:
// hide non-subsurface, non-site card meshes so burrowed/submerged
// units stand out against the highlighted region bodies. Sites
// stay visible because they're the spatial anchors players read
// to identify cells in subsurface view.
//
// A view toggle starts a transition tween (see beginViewTransition)
// so surface units float up + fade out when entering subsurface
// view and rise from below + fade in when returning to surface.
// syncMeshFromBody clears any view Y offset at the top of every
// frame, so this tick re-applies it without polluting _applyComputedY's
// tween state.
export function applyRegionViewVisibility(host) {
  const subsurface = host.state.regionView === 'subsurface';
  const focusedKey = host.state.focusedCell;
  const trans = host._viewTransition;
  const now = performance.now();

  let elapsed = 0;
  let active = false;
  if (trans) {
    elapsed = now - trans.startTime;
    active = elapsed < trans.totalDuration;
  }

  for (const [cardId, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;

    const isDragging = host.dragging?.draggingIds?.has(cardId);
    // A "surface unit" here is anything the subsurface view would
    // normally hide: not a site, not currently subsurface. Cards in
    // the user's hand-to-table drag stay visible regardless so the
    // player can see what they're placing.
    const isSurfaceUnit = !card.subsurface && !card.isSite;
    // A member of the FOCUSED cell's fan is always visible: the fan shows
    // the whole square (both regions) regardless of the current view.
    const inFocusedFan = !!focusedKey && !!card.cell
      && `${card.cell.col},${card.cell.row}` === focusedKey;
    const affected = isSurfaceUnit && !isDragging && !inFocusedFan;

    if (!affected) {
      host._clearViewVisualState(mesh, cardId);
      if (mesh.userData._hiddenByRegionView) {
        mesh.visible = true;
        mesh.userData._hiddenByRegionView = false;
        host._setCardEffectsVisible(cardId, true);
      }
      continue;
    }

    const motion = active && trans
      ? host._computeCardViewMotion(trans, cardId, elapsed)
      : null;

    if (!subsurface) {
      // Surface view (resting) or fading in from subsurface.
      if (motion && trans.target === 'surface') {
        // Descend from above + fade in along this card's seeded path.
        mesh.visible = true;
        if (mesh.userData._hiddenByRegionView) {
          mesh.userData._hiddenByRegionView = false;
          host._setCardEffectsVisible(cardId, true);
        }
        host._setViewVisualState(
          mesh, cardId, motion.opacity, motion.yOff,
        );
      } else {
        host._clearViewVisualState(mesh, cardId);
        if (mesh.userData._hiddenByRegionView) {
          mesh.visible = true;
          mesh.userData._hiddenByRegionView = false;
          host._setCardEffectsVisible(cardId, true);
        }
      }
    } else {
      // Subsurface view. Either lifting/fading out or fully hidden.
      if (motion && trans.target === 'subsurface') {
        mesh.visible = true;
        host._setViewVisualState(
          mesh, cardId, motion.opacity, motion.yOff,
        );
      } else {
        host._clearViewVisualState(mesh, cardId);
        if (mesh.visible) {
          mesh.visible = false;
          mesh.userData._hiddenByRegionView = true;
          host._setCardEffectsVisible(cardId, false);
        }
      }
    }
  }

  if (trans && !active) host._viewTransition = null;
}

// Lazily-seeded per-card motion for the region-view transition.
// Each card gets a stable random delay so the board reads as a
// flock launching in rolling waves rather than a rigid block. The
// trajectory itself is identical for every card. Straight up, same
// height, same speed. Only the start time differs.
//
// Easing curves (localT runs 0→1 over `trans.duration` after the
// card's delay elapses):
//
//   out lift:    1 - (1-localT)^3   (easeOutCubic. Fast launch, soft apex)
//   out opacity: 1 - localT^4       (delayed fade. Opaque until ≈0.6)
//
//   in  lift:    (1-localT)^3       (fast drop, soft landing)
//   in  opacity: 1 - (1-localT)^4   (early fade-in. Visible by ≈0.2)
export function computeCardViewMotion(host, trans, cardId, elapsed) {
  let seed = trans.seeds.get(cardId);
  if (!seed) {
    seed = { delay: Math.random() * trans.staggerWindow };
    trans.seeds.set(cardId, seed);
  }

  const localT = Math.max(0, Math.min(1, (elapsed - seed.delay) / trans.duration));
  const oneMinusT = 1 - localT;
  const oneMinusTCub = oneMinusT * oneMinusT * oneMinusT;
  const oneMinusTQ4  = oneMinusT * oneMinusT * oneMinusT * oneMinusT;

  const isOut = trans.target === 'subsurface';
  const liftCurve = isOut ? (1 - oneMinusTCub) : oneMinusTCub;
  const opacityFactor = isOut
    ? 1 - localT * localT * localT * localT
    : 1 - oneMinusTQ4;

  return {
    opacity: opacityFactor,
    yOff: liftCurve * VIEW_TRANSITION_LIFT,
  };
}

// Apply opacity + Y offset for the view transition.
// mesh.position.y is pristine when this runs: syncMeshFromBody
// copied body.y → mesh.y at the top of the frame, and
// _applyComputedY's body.y write happens before any view offset is
// added. So the previous frame's offset has already been wiped
// from mesh.y and we just add the current frame's offset on top.
//
// Fade everything tied to the card as a single visual unit:
//   - front + back face materials on the mesh itself
//   - every child of the mesh (hover stroke, ownership outline,
//     attack/HP pill HUDs, status badges)
//   - every per-card particle/glow effect mounted on the scene
// Each material's baseline opacity is cached on first call so we
// can restore it cleanly in _clearViewVisualState. Pills baseline
// at 0.85, badges at 1, etc., and the multiplier preserves their
// relative weights.
//
// Three.js shadow maps don't fade with material.opacity (they're
// generated from texture alpha, which we're not touching), so we
// disable castShadow for the duration of the transition. Without
// this the shadow ghost-renders solid on the table while the card
// above it dissolves. The exact "shadow lingers" artifact.
export function setViewVisualState(host, mesh, cardId, opacity, yOff) {
  mesh.position.y += yOff;
  mesh.userData._viewYOffset = yOff;

  // Per-card front/back materials are at indices 0/1; the edge
  // material at index 2 is shared across all cards so we don't
  // touch it (it'd dim every other card on the board).
  //
  // alphaTest: 0.5 on the card materials would cause the fragment
  // shader to `discard` every textured pixel as soon as
  // material.opacity drops under ~0.5. The card pops to invisible
  // mid-fade while its HUDs/badges/effects keep fading smoothly.
  // Cache the baseline alphaTest and clear it for the transition;
  // _clearViewVisualState restores it.
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (let i = 0; i < 2; i++) {
    const m = mats[i];
    if (!m) continue;
    m.opacity = opacity;
    if (m.userData?._viewBaseAlphaTest === undefined && m.alphaTest > 0) {
      if (!m.userData) m.userData = {};
      m.userData._viewBaseAlphaTest = m.alphaTest;
      m.alphaTest = 0;
      m.needsUpdate = true;
    }
  }

  // Fade every in-card overlay: HUD pills, status badges, hover
  // stroke, ownership outline. They're all children of the mesh.
  host._fadeChildOverlays(mesh, opacity);

  // Per-card particle/glow effects live on the scene (not parented
  // to the mesh). Fade them through their full subtree so the
  // visual "card unit" includes its airborne wind, charge bolts,
  // etc.
  host._fadeCardEffects(cardId, opacity);

  // Drop the cast shadow for the duration of the transition. We
  // restore it in _clearViewVisualState. Cheap: castShadow is a
  // boolean read every frame by the shadow pass.
  if (mesh.castShadow) {
    mesh.userData._viewBaseCastShadow = true;
    mesh.castShadow = false;
  }
}

export function clearViewVisualState(host, mesh, cardId) {
  // mesh.y has already been reset to the pristine rank Y by
  // syncMeshFromBody at the top of the frame (and rewritten by
  // _applyComputedY), so the previous frame's view offset is no
  // longer present. Just clear the marker.
  if (mesh.userData._viewYOffset) mesh.userData._viewYOffset = 0;

  // Restore card face opacity + alphaTest. Materials are stored in
  // the same indices as in _setViewVisualState (0 = front, 1 = back).
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (let i = 0; i < 2; i++) {
    const m = mats[i];
    if (!m) continue;
    if (m.opacity !== 1) m.opacity = 1;
    if (m.userData?._viewBaseAlphaTest !== undefined) {
      m.alphaTest = m.userData._viewBaseAlphaTest;
      m.userData._viewBaseAlphaTest = undefined;
      m.needsUpdate = true;
    }
  }

  host._fadeChildOverlays(mesh, 1, { restore: true });
  host._fadeCardEffects(cardId, 1, { restore: true });

  if (mesh.userData._viewBaseCastShadow) {
    mesh.castShadow = true;
    mesh.userData._viewBaseCastShadow = false;
  }
}

// Multiply every child material's opacity by `factor`. Caches the
// baseline opacity per material on the first call so the original
// value is restored on `restore: true` regardless of how many
// transition frames stomped it. Idempotent. Safe to call every
// frame.
export function fadeChildOverlays(host, mesh, factor, { restore = false } = {}) {
  for (const child of mesh.children) {
    const list = Array.isArray(child.material) ? child.material : [child.material];
    for (const m of list) {
      if (!m) continue;
      if (restore) {
        if (m.userData?._viewOpacityBase !== undefined) {
          m.opacity = m.userData._viewOpacityBase;
          m.userData._viewOpacityBase = undefined;
        }
        continue;
      }
      if (!m.userData) m.userData = {};
      if (m.userData._viewOpacityBase === undefined) {
        m.userData._viewOpacityBase = m.opacity ?? 1;
      }
      m.opacity = m.userData._viewOpacityBase * factor;
      m.transparent = true;
    }
  }
}

// Per-card status effects (airborne wind, burrowing earth, charge
// lightning, stealth smoke, ranged bow, etc.) every render through
// their own custom `ShaderMaterial`. Three.js does NOT pipe
// `material.opacity` into custom fragment shaders. The GPU only
// sees what the shader writes to gl_FragColor. So setting
// `material.opacity` on them is a no-op and they stay fully visible
// while the card and HUDs fade.
//
// Fix: patch each ShaderMaterial *once* with a `uOpacity` uniform
// and a trailing `gl_FragColor.a *= uOpacity;` injection in the
// fragment shader. The patch is permanent (uOpacity defaults to 1,
// so the multiply is a no-op outside transitions). During a
// transition we just drive the uniform value.
//
// MeshBasicMaterial / MeshStandardMaterial children still go through
// the regular `material.opacity` channel since Three.js's built-in
// shaders honor it.
export function fadeCardEffects(host, cardId, factor, { restore = false } = {}) {
  const effects = host.cardEffects?.get(cardId);
  if (!effects) return;
  for (const [, effect] of effects) {
    effect.points?.traverse((child) => {
      if (!child.material) return;
      const list = Array.isArray(child.material) ? child.material : [child.material];
      for (const m of list) {
        if (!m) continue;
        if (m.isShaderMaterial || m.uniforms) {
          host._ensureShaderOpacityUniform(m);
          if (m.uniforms?.uOpacity) m.uniforms.uOpacity.value = restore ? 1 : factor;
          continue;
        }
        if (restore) {
          if (m.userData?._viewOpacityBase !== undefined) {
            m.opacity = m.userData._viewOpacityBase;
            m.userData._viewOpacityBase = undefined;
          }
          continue;
        }
        if (!m.userData) m.userData = {};
        if (m.userData._viewOpacityBase === undefined) {
          m.userData._viewOpacityBase = m.opacity ?? 1;
        }
        m.opacity = m.userData._viewOpacityBase * factor;
        m.transparent = true;
      }
    });
  }
}

// Inject `uOpacity` into a ShaderMaterial: adds the uniform, prepends
// its declaration in the fragment shader, and appends a final-alpha
// multiplication right before main()'s closing brace. Idempotent.
// marks the material on first run so repeated calls are no-ops.
// Triggers one shader recompile via `needsUpdate = true`.
export function ensureShaderOpacityUniform(host, material) {
  if (!material) return;
  if (material.userData?._uOpacityPatched) return;
  if (!material.uniforms) material.uniforms = {};
  if (!material.fragmentShader) return;

  if (!material.uniforms.uOpacity) {
    material.uniforms.uOpacity = { value: 1 };
  }

  let frag = material.fragmentShader;
  // Idempotency guard against pre-patched shaders.
  if (!/uniform\s+float\s+uOpacity\s*;/.test(frag)) {
    frag = `uniform float uOpacity;\n${frag}`;
  }
  // Inject the alpha multiply just before the final closing brace
  // of main(). The convention across every effect in this codebase
  // is to end main() with `gl_FragColor = vec4(rgb, alpha);` so a
  // line of `gl_FragColor.a *= uOpacity;` right after that is safe.
  const lastBrace = frag.lastIndexOf('}');
  if (lastBrace > 0) {
    frag = frag.slice(0, lastBrace)
      + '  gl_FragColor.a *= uOpacity;\n'
      + frag.slice(lastBrace);
  }
  material.fragmentShader = frag;

  if (!material.userData) material.userData = {};
  material.userData._uOpacityPatched = true;
  material.transparent = true;
  material.needsUpdate = true;
}

export function beginViewTransition(host, target) {
  // Per-card seeds (just a random takeoff delay) are lazily populated
  // by _applyRegionViewVisibility the first frame each card is
  // observed and reused for the rest of this transition. Fresh seeds
  // per transition mean cards take off in a different stagger pattern
  // each time, which reads as more organic than a fixed order.
  host._viewTransition = {
    startTime: performance.now(),
    duration: VIEW_TRANSITION_DURATION_MS,
    staggerWindow: VIEW_TRANSITION_STAGGER_MS,
    totalDuration: VIEW_TRANSITION_DURATION_MS + VIEW_TRANSITION_STAGGER_MS,
    target,
    seeds: new Map(),
  };
}

// v1.111. Flip the visibility of every per-card visual effect for
// `cardId`. Used by _applyRegionViewVisibility so a hidden mesh's
// particles/glows hide with it. Idempotent and safe when the card
// has no effects.
export function setCardEffectsVisible(host, cardId, visible) {
  const effects = host.cardEffects?.get(cardId);
  if (!effects) return;
  for (const [, effect] of effects) {
    if (effect?.points) effect.points.visible = visible;
  }
}

/**
 * Lightweight count of water-cell vs underground-cell totals for the
 * cave/underwater ambience mix. Goes through the same
 * connectedSubsurfaceComponents scan as `buildRegionViewCells` (so
 * Rubble breaks bodies the same way), but doesn't allocate per-cell
 * entries. We just sum the body sizes. Called both at transition
 * start (to seed the initial mix) and every frame in subsurface (to
 * react to sites being played/removed mid-session).
 */
export function countSubsurfaceCells(host) {
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return { water: 0, underground: 0 };
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const getFullCard = host._getFullCard;

  let water = 0;
  for (const body of connectedSubsurfaceComponents(
    host.meshes, host._cellAtWorld, totalCols, totalRows, getFullCard, 'underwater',
  )) {
    water += body.size;
  }
  let underground = 0;
  for (const body of connectedSubsurfaceComponents(
    host.meshes, host._cellAtWorld, totalCols, totalRows, getFullCard, 'underground',
  )) {
    underground += body.size;
  }
  return { water, underground };
}

/**
 * Per-frame sync of the subsurface region-view tint meshes. Each
 * cell currently in `buildRegionViewCells` gets a transparent plane
 * (a `ShaderMaterial`-shaded quad) parented to scene.scene, hugged
 * just above the playmat surface. Cards / sites render on top
 * because tint meshes have `renderOrder = -100` and `depthWrite: false`.
 *
 * - New cells: spawn a mesh of the appropriate kind (water / underground).
 * - Existing cells: refresh the four vertex positions in place; cell
 *   layouts can change at runtime so we can't bake geometry at create.
 * - Vanished cells: remove + dispose to avoid GPU resource leaks.
 * - Time uniform: advances every frame to drive the gentle pulse.
 *
 * Cheap when not in subsurface view. `buildRegionViewCells` returns
 * [] and the orphan-removal loop disposes any leftover meshes from a
 * previous subsurface session.
 */
export function syncRegionViewTints(host) {
  if (!host.scene?.scene) return;
  const cells = host.buildRegionViewCells();
  const wanted = new Set(cells.map((c) => c.id));

  // Reap meshes whose cell is no longer in the body list (e.g.
  // toggled back to surface view, or a site moved off this cell).
  for (const [id, mesh] of host.regionViewTintMeshes) {
    if (!wanted.has(id)) {
      host.scene.scene.remove(mesh);
      disposeRegionTintMesh(mesh);
      host.regionViewTintMeshes.delete(id);
    }
  }

  if (cells.length === 0) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;

  const now = performance.now() * 0.001;
  for (const cell of cells) {
    let mesh = host.regionViewTintMeshes.get(cell.id);
    if (!mesh) {
      mesh = createRegionTintMesh(cell.kind);
      host.scene.scene.add(mesh);
      host.regionViewTintMeshes.set(cell.id, mesh);
    }
    const [colStr, rowStr] = cell.cellKey.split(',');
    const cellGeo = sampleCellGeometry(grid, parseInt(colStr, 10), parseInt(rowStr, 10));
    if (cellGeo) updateRegionTintGeometry(mesh, cellGeo);
    if (cell.edges) updateRegionTintEdges(mesh, cell.edges);
    if (mesh.material.uniforms?.uTime) {
      mesh.material.uniforms.uTime.value = now;
    }
  }
}

export function disposeRegionViewTints(host) {
  if (!host.regionViewTintMeshes) return;
  for (const [, mesh] of host.regionViewTintMeshes) {
    host.scene?.scene?.remove(mesh);
    disposeRegionTintMesh(mesh);
  }
  host.regionViewTintMeshes.clear();
}

/**
 * Drive every subsurface-aware scene element from one source: the
 * playmat crossfade, the candle fade, and the firefly lift + fade
 * all read the state we hand the scene each frame.
 *
 * `progress` is the global smoothstep-eased value 0..1 (0 = surface
 * fully shown, 1 = subsurface fully shown). The candles and playmat
 * use this directly. The fireflies need the full transition object
 * because they apply per-firefly staggered delays. Matching the
 * card flight. So the scene reads `transition` to compute each
 * firefly's own local progress.
 */
export function syncSubsurfaceSceneState(host) {
  if (!host.scene?.setSubsurfaceState) return;
  const trans = host._viewTransition;
  let progress = host.state.regionView === 'subsurface' ? 1 : 0;
  if (trans) {
    const t = Math.min(1, (performance.now() - trans.startTime) / trans.totalDuration);
    const eased = t * t * (3 - 2 * t);
    progress = trans.target === 'subsurface' ? eased : 1 - eased;
  }
  host.scene.setSubsurfaceState({ progress, transition: trans });

  // Push the live water/underground ratio to the cave + underwater
  // ambience layers so the mix tracks the board state. Sites added
  // or removed mid-session re-balance the audio. setSubsurfaceMix
  // dedupes when counts are unchanged, so the per-frame call is
  // cheap when the board is stable.
  if (host.state.regionView === 'subsurface') {
    const counts = host._countSubsurfaceCells();
    setSubsurfaceMix(counts.water, counts.underground);
  }
}
